import { basicsLessons } from "./basicsLessons.js";
export function lessonCopy(copy, locale) {
    return locale === "sv" && copy.sv ? copy.sv : copy.en;
}
/** Authored content can fall back to English without changing the UI locale. */
export function lessonContentLanguage(lesson, locale) {
    const copies = [
        lesson.title,
        lesson.intro,
        ...[lesson.example, ...lesson.exercises].flatMap((exercise) => {
            var _a;
            return [
                exercise.question,
                exercise.explanation,
                ...(((_a = exercise.choices) === null || _a === void 0 ? void 0 : _a.map((choice) => choice.label)) || []),
            ];
        }),
    ];
    return locale === "sv" && copies.every((copy) => Boolean(copy.sv))
        ? "sv"
        : "en";
}
export const foundationLessonId = (slug) => "foundation:" + slug;
export function getLearningLesson(slug) {
    return learningLessons.find((lesson) => lesson.slug === slug);
}
export function lessonForReviewCategory(category) {
    const topics = {
        fork: "forks",
        pin: "pins",
        skewer: "skewers",
        "discovered attack": "discovered-attacks",
        "discovered check": "discovered-attacks",
        "back rank mate": "back-rank-mates",
        "back rank checkmate": "back-rank-mates",
        checkmate: "basic-checkmates",
        "threaten checkmate": "basic-checkmates",
        hanging: "hanging-pieces",
        "direct material win": "hanging-pieces",
        "hanging piece": "hanging-pieces",
        "win material": "hanging-pieces",
        development: "development",
        "develop piece": "development",
        "develop queen early": "development",
        "move developed piece": "development",
        "control center": "centre-control",
        "control centre": "centre-control",
        "center control": "centre-control",
        "king safety": "king-safety",
        castle: "king-safety",
        "piece activity": "piece-activity",
        opposition: "king-and-pawn",
        promotion: "king-and-pawn",
        "passed pawn": "king-and-pawn",
    };
    return getLearningLesson(topics[(category || "").toLowerCase()] || "");
}
export function completedExerciseIndices(value, total) {
    try {
        const parsed = typeof value === "string" ? JSON.parse(value) : value;
        return Array.isArray(parsed)
            ? [
                ...new Set(parsed.filter((index) => Number.isSafeInteger(index) && index >= 0 && index < total)),
            ].sort((a, b) => a - b)
            : [];
    }
    catch (_a) {
        return [];
    }
}
const foundationLessons = [
    {
        slug: "hanging-pieces",
        group: "tactics",
        title: {
            en: "Spot undefended pieces",
            sv: "Upptäck oskyddade pjäser",
        },
        intro: {
            en: "Before planning an attack, look for a piece you can capture safely. Check what protects the target and what could recapture your piece.",
            sv: "Innan du planerar ett angrepp, leta efter en pjäs du kan ta säkert. Kontrollera vad som skyddar målet och vad som kan slå tillbaka.",
        },
        example: {
            fen: "6k1/5ppp/5q2/8/4N3/8/5PPP/6K1 w - - 0 1",
            moves: ["e4f6"],
            question: {
                en: "Find an undefended queen.",
                sv: "Hitta en oskyddad dam.",
            },
            explanation: {
                en: "Nxf6 wins the queen. Nothing can recapture the knight.",
                sv: "Sxf6 vinner damen. Ingen pjäs kan slå tillbaka springaren.",
            },
        },
        exercises: [
            {
                fen: "6k1/5ppp/8/3q4/8/8/5PPP/3R2K1 w - - 0 1",
                moves: ["d1d5"],
                question: {
                    en: "Capture the undefended queen.",
                    sv: "Ta den oskyddade damen.",
                },
                explanation: {
                    en: "Rxd5 wins the queen. Scan the whole file before moving.",
                    sv: "Txd5 vinner damen. Titta längs hela linjen innan du flyttar.",
                },
            },
            {
                fen: "7k/5rpp/8/8/2B5/8/5PPP/6K1 w - - 0 1",
                moves: ["c4f7"],
                question: {
                    en: "Which valuable piece can your bishop take safely?",
                    sv: "Vilken värdefull pjäs kan löparen ta säkert?",
                },
                explanation: {
                    en: "Bxf7 wins a rook. The black king is too far away to recapture.",
                    sv: "Lxf7 vinner ett torn. Den svarta kungen är för långt bort för att slå tillbaka.",
                },
            },
            {
                fen: "2r3k1/5ppp/8/8/2B5/8/5PPP/6K1 b - - 0 1",
                moves: ["c8c4"],
                question: {
                    en: "Black to move: find the free bishop.",
                    sv: "Svart vid draget: hitta den oskyddade löparen.",
                },
                explanation: {
                    en: "Rxc4 wins the undefended bishop. Always check your opponent’s loose pieces.",
                    sv: "Txc4 vinner den oskyddade löparen. Leta alltid efter motståndarens lösa pjäser.",
                },
            },
        ],
    },
    {
        slug: "forks",
        group: "tactics",
        title: {
            en: "Attack two targets",
            sv: "Angrip två mål",
        },
        intro: {
            en: "A fork attacks two pieces at once. A check is especially useful: your opponent must save the king, leaving the other target behind.",
            sv: "En gaffel angriper två pjäser samtidigt. En schack är särskilt användbar: motståndaren måste rädda kungen och lämnar det andra målet.",
        },
        example: {
            fen: "r3k3/5ppp/8/1N6/8/8/5PPP/6K1 w - - 0 1",
            moves: ["b5c7"],
            question: {
                en: "Give check and attack the rook at the same time.",
                sv: "Schacka och angrip tornet samtidigt.",
            },
            explanation: {
                en: "Nc7+ attacks e8 and a8. After the king moves, the knight can take the rook.",
                sv: "Sc7+ angriper e8 och a8. När kungen flyttar kan springaren ta tornet.",
            },
        },
        exercises: [
            {
                fen: "r3k3/5ppp/8/3N4/8/8/5PPP/6K1 w - - 0 1",
                moves: ["d5c7"],
                question: {
                    en: "Find a knight fork of king and rook.",
                    sv: "Hitta en springargaffel på kung och torn.",
                },
                explanation: {
                    en: "Nc7+ forces a king response while attacking the rook on a8.",
                    sv: "Sc7+ tvingar kungen att reagera och angriper tornet på a8.",
                },
            },
            {
                fen: "3q3k/5ppp/8/4N3/8/8/5PPP/6K1 w - - 0 1",
                moves: ["e5f7"],
                question: {
                    en: "Fork the king and queen.",
                    sv: "Gaffla kungen och damen.",
                },
                explanation: {
                    en: "Nf7+ checks h8 and attacks d8. The queen is the second target.",
                    sv: "Sf7+ schackar h8 och angriper d8. Damen är det andra målet.",
                },
            },
            {
                fen: "3r3k/5ppp/8/8/3n4/8/3Q1PPP/4K3 b - - 0 1",
                moves: ["d4f3"],
                question: {
                    en: "Black to move: fork the king and queen.",
                    sv: "Svart vid draget: gaffla kungen och damen.",
                },
                explanation: {
                    en: "Nf3+ attacks the king on e1 and queen on d2.",
                    sv: "Sf3+ angriper kungen på e1 och damen på d2.",
                },
            },
        ],
    },
    {
        slug: "pins",
        group: "tactics",
        title: {
            en: "Use a pinned defender",
            sv: "Utnyttja en bunden försvarare",
        },
        intro: {
            en: "A piece pinned to its king cannot leave the line of attack. A target it appears to defend may therefore be safe to capture.",
            sv: "En pjäs som är bunden mot sin kung kan inte lämna angreppslinjen. Ett mål som den verkar försvara kan därför vara säkert att ta.",
        },
        example: {
            fen: "4k3/4nppp/8/5r2/6B1/8/5PPP/4R1K1 w - - 0 1",
            moves: ["g4f5"],
            question: {
                en: "Can the knight really defend the rook?",
                sv: "Kan springaren verkligen försvara tornet?",
            },
            explanation: {
                en: "Bxf5 wins the rook. Ne7 is pinned by Re1 and cannot recapture without exposing its king.",
                sv: "Lxf5 vinner tornet. Se7 är bunden av Te1 och får inte slå tillbaka eftersom kungen då blir schackad.",
            },
        },
        exercises: [
            {
                fen: "4k3/4nppp/8/5r2/8/7B/5PPP/4R2K w - - 0 1",
                moves: ["h3f5"],
                question: {
                    en: "Take the rook that the pinned knight cannot defend.",
                    sv: "Ta tornet som den bundna springaren inte kan försvara.",
                },
                explanation: {
                    en: "Bxf5 is safe: moving Ne7 would uncover a rook check on e8.",
                    sv: "Lxf5 är säkert: om Se7 flyttar öppnas en tornschack på e8.",
                },
            },
            {
                fen: "3k4/5ppp/5n2/6B1/4r3/3B4/5PPP/6K1 w - - 0 1",
                moves: ["d3e4"],
                question: {
                    en: "Use the bishop pin to win material.",
                    sv: "Utnyttja löparbindningen för att vinna material.",
                },
                explanation: {
                    en: "Bxe4 wins the rook. The knight on f6 is pinned along g5–d8.",
                    sv: "Lxe4 vinner tornet. Springaren på f6 är bunden längs g5–d8.",
                },
            },
            {
                fen: "6k1/5ppp/6b1/8/1b2R3/2N5/5PPP/4K3 b - - 0 1",
                moves: ["g6e4"],
                question: {
                    en: "Black to move: which defender is pinned?",
                    sv: "Svart vid draget: vilken försvarare är bunden?",
                },
                explanation: {
                    en: "Bxe4 wins the rook because Nc3 cannot leave the b4–e1 diagonal.",
                    sv: "Lxe4 vinner tornet eftersom Sc3 inte får lämna diagonalen b4–e1.",
                },
            },
        ],
    },
    {
        slug: "skewers",
        group: "tactics",
        title: {
            en: "Look behind the king",
            sv: "Se vad som står bakom kungen",
        },
        intro: {
            en: "A skewer attacks a valuable piece in front of another target. If you check a king along a line, it must move and can expose the piece behind it.",
            sv: "Ett spett angriper en värdefull pjäs framför ett annat mål. Schackar du kungen längs en linje måste den flytta och kan blotta pjäsen bakom.",
        },
        example: {
            fen: "q7/8/k7/8/1R6/8/8/6K1 w - - 0 1",
            moves: ["b4a4"],
            question: {
                en: "Give a rook check along the queen’s file.",
                sv: "Schacka med tornet på damens linje.",
            },
            explanation: {
                en: "Ra4+ forces the king off the a-file. The rook can then capture the queen behind it.",
                sv: "Ta4+ tvingar kungen bort från a-linjen. Tornet kan sedan ta damen bakom.",
            },
        },
        exercises: [
            {
                fen: "8/8/8/2B3q1/5k2/8/5PP1/6K1 w - - 0 1",
                moves: ["c5e3"],
                question: {
                    en: "Check the king with a protected bishop.",
                    sv: "Schacka kungen med en skyddad löpare.",
                },
                explanation: {
                    en: "Be3+ skewers the queen on g5. The pawn on f2 protects the bishop from the king.",
                    sv: "Le3+ gör ett spett mot damen på g5. Bonden på f2 skyddar löparen mot kungen.",
                },
            },
            {
                fen: "2q5/8/2k5/8/R7/8/8/6K1 w - - 0 1",
                moves: ["a4c4"],
                question: {
                    en: "Use the rook to expose the queen behind the king.",
                    sv: "Använd tornet för att blotta damen bakom kungen.",
                },
                explanation: {
                    en: "A check on the c-file forces the king away and uncovers Qc8.",
                    sv: "En schack på c-linjen tvingar bort kungen och blottar Dc8.",
                },
            },
            {
                fen: "6k1/5pp1/8/5K2/2b3Q1/8/8/8 b - - 0 1",
                moves: ["c4e6"],
                question: {
                    en: "Black to move: give a protected bishop check.",
                    sv: "Svart vid draget: schacka med en skyddad löpare.",
                },
                explanation: {
                    en: "Be6+ skewers the queen on g4. The f7 pawn protects the bishop.",
                    sv: "Le6+ gör ett spett mot damen på g4. Bonden på f7 skyddar löparen.",
                },
            },
        ],
    },
    {
        slug: "discovered-attacks",
        group: "tactics",
        title: {
            en: "Uncover an attack",
            sv: "Öppna ett angrepp",
        },
        intro: {
            en: "Moving one piece can uncover an attack by another. Look for a move that opens a check while also threatening a second target.",
            sv: "När en pjäs flyttar kan den öppna ett angrepp från en annan. Leta efter ett drag som öppnar en schack och samtidigt hotar ett andra mål.",
        },
        example: {
            fen: "q3k3/5ppp/8/8/4B3/8/5PPP/4R1K1 w - - 0 1",
            moves: ["e4d5"],
            question: {
                en: "Open a rook check and aim at the queen.",
                sv: "Öppna en tornschack och sikta på damen.",
            },
            explanation: {
                en: "Bd5+ uncovers Re1’s check. The bishop also attacks Qa8 along d5–a8.",
                sv: "Ld5+ öppnar Te1:s schack. Löparen angriper också Da8 längs d5–a8.",
            },
        },
        exercises: [
            {
                fen: "4k3/5ppp/8/7q/4N3/8/5PPP/4R1K1 w - - 0 1",
                moves: ["e4g3"],
                question: {
                    en: "Move the knight to uncover check and attack the queen.",
                    sv: "Flytta springaren för att öppna schack och angripa damen.",
                },
                explanation: {
                    en: "Ng3+ opens the e-file check and attacks the queen on h5. If the queen blocks the rook’s check, the rook can capture it.",
                    sv: "Sg3+ öppnar schacken på e-linjen och angriper damen på h5. Om damen blockerar tornets schack kan tornet ta den.",
                },
            },
            {
                fen: "4r1k1/5ppp/8/4b3/8/8/5PPP/Q3K3 b - - 0 1",
                moves: ["e5d4"],
                question: {
                    en: "Black to move: uncover check with a second threat.",
                    sv: "Svart vid draget: öppna schack med ett andra hot.",
                },
                explanation: {
                    en: "Bd4+ opens Re8’s check and aims at the queen on a1.",
                    sv: "Ld4+ öppnar Te8:s schack och siktar på damen på a1.",
                },
            },
            {
                fen: "3k4/5pqp/8/8/3N4/8/5PPP/3R2K1 w - - 0 1",
                moves: ["d4e6"],
                question: {
                    en: "Open the rook’s file while threatening the queen.",
                    sv: "Öppna tornets linje och hota samtidigt damen.",
                },
                explanation: {
                    en: "Ne6+ uncovers the d-file check and attacks Qg7.",
                    sv: "Se6+ öppnar schacken på d-linjen och angriper Dg7.",
                },
            },
        ],
    },
    {
        slug: "back-rank-mates",
        group: "tactics",
        title: {
            en: "Recognize back-rank mate",
            sv: "Känn igen korridormatt",
        },
        intro: {
            en: "A king trapped behind its own pawns may have no escape from a rook or queen check along the back rank. Check every escape square.",
            sv: "En kung som är instängd bakom sina egna bönder kan sakna flyktruta mot en torn- eller damschack på sista raden. Kontrollera varje flyktruta.",
        },
        example: {
            fen: "6k1/5ppp/8/8/8/8/8/3Q2K1 w - - 0 1",
            moves: ["d1d8"],
            question: {
                en: "Find a queen checkmate on the back rank.",
                sv: "Hitta en korridormatt med damen.",
            },
            explanation: {
                en: "Qd8# covers the eighth rank. Black’s pawns block the king’s escape.",
                sv: "Dd8# täcker åttonde raden. Svarts bönder stänger kungens flyktvägar.",
            },
        },
        exercises: [
            {
                fen: "6k1/5ppp/8/8/8/8/8/4R1K1 w - - 0 1",
                moves: ["e1e8"],
                question: {
                    en: "Deliver back-rank mate with the rook.",
                    sv: "Gör korridormatt med tornet.",
                },
                explanation: {
                    en: "Re8# leaves no safe king move and no way to block or capture the rook.",
                    sv: "Te8# lämnar inget säkert kungsdrag och inget sätt att blockera eller ta tornet.",
                },
            },
            {
                fen: "7k/6pp/8/8/8/8/8/R5K1 w - - 0 1",
                moves: ["a1a8"],
                question: {
                    en: "Check the trapped king along the eighth rank.",
                    sv: "Schacka den instängda kungen längs åttonde raden.",
                },
                explanation: {
                    en: "Ra8# controls g8 while the pawns occupy g7 and h7.",
                    sv: "Ta8# kontrollerar g8 medan bönderna står på g7 och h7.",
                },
            },
            {
                fen: "2r3k1/8/8/8/8/8/5PPP/6K1 b - - 0 1",
                moves: ["c8c1"],
                question: {
                    en: "Black to move: find back-rank mate.",
                    sv: "Svart vid draget: hitta korridormatt.",
                },
                explanation: {
                    en: "Rc1# traps the white king behind its pawns.",
                    sv: "Tc1# fångar den vita kungen bakom dess bönder.",
                },
            },
        ],
    },
    {
        slug: "development",
        group: "strategy",
        title: {
            en: "Bring your pieces into play",
            sv: "Utveckla dina pjäser",
        },
        intro: {
            en: "Develop your knights and bishops toward useful squares. Several healthy moves can work: the aim is to bring new pieces into play, rather than keep moving the same one.",
            sv: "Utveckla springare och löpare till användbara rutor. Flera bra drag kan fungera: målet är att få med nya pjäser i spelet, i stället för att flytta samma pjäs igen.",
        },
        example: {
            fen: "rnbqkbnr/pppp1ppp/8/4p3/4P3/8/PPPP1PPP/RNBQKBNR w KQkq - 0 2",
            moves: ["g1f3", "b1c3"],
            question: {
                en: "Develop a knight toward the centre.",
                sv: "Utveckla en springare mot centrum.",
            },
            explanation: {
                en: "Nf3 attacks e5 and Nc3 supports e4. Both develop a new piece toward central squares.",
                sv: "Sf3 angriper e5 och Sc3 stödjer e4. Båda utvecklar en ny pjäs mot centrum.",
            },
        },
        exercises: [
            {
                fen: "rnbqkbnr/ppp1pppp/8/3p4/3P4/8/PPP1PPPP/RNBQKBNR w KQkq - 0 2",
                moves: ["g1f3", "b1c3"],
                question: {
                    en: "Bring a knight toward the centre.",
                    sv: "För en springare mot centrum.",
                },
                explanation: {
                    en: "Nf3 and Nc3 both develop a knight. Your choice also shapes your later pawn plan.",
                    sv: "Sf3 och Sc3 utvecklar båda en springare. Valet påverkar också din senare bondeplan.",
                },
            },
            {
                fen: "r1bqkbnr/pppp1ppp/2n5/4p3/4P3/5N2/PPPP1PPP/RNBQKB1R w KQkq - 2 3",
                moves: ["f1c4", "f1b5"],
                question: {
                    en: "Develop your king’s bishop actively.",
                    sv: "Utveckla kungslöparen aktivt.",
                },
                explanation: {
                    en: "Bc4 targets f7; Bb5 pressures Nc6. Both clear f1 so you can castle later.",
                    sv: "Lc4 siktar på f7; Lb5 sätter press på Sc6. Båda tömmer f1 så att du kan rockera senare.",
                },
            },
            {
                fen: "rnbqkbnr/pppp1ppp/8/4p3/4P3/5N2/PPPP1PPP/RNBQKB1R b KQkq - 1 2",
                moves: ["b8c6", "g8f6"],
                question: {
                    en: "Black to move: develop a knight.",
                    sv: "Svart vid draget: utveckla en springare.",
                },
                explanation: {
                    en: "Nc6 defends e5 and Nf6 attacks e4. Both bring a new piece into play.",
                    sv: "Sc6 försvarar e5 och Sf6 angriper e4. Båda får med en ny pjäs i spelet.",
                },
            },
        ],
    },
    {
        slug: "centre-control",
        group: "strategy",
        title: {
            en: "Fight for the centre",
            sv: "Kämpa om centrum",
        },
        intro: {
            en: "Central pawns and pieces influence more of the board. Occupy the centre when possible and challenge an opponent’s pawn centre when it becomes too comfortable.",
            sv: "Centrala bönder och pjäser påverkar mer av brädet. Ta plats i centrum när det går och utmana motståndarens bondecentrum när det blir för bekvämt.",
        },
        example: {
            fen: "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1",
            moves: ["e2e4", "d2d4"],
            question: {
                en: "Occupy the centre with a pawn.",
                sv: "Ta plats i centrum med en bonde.",
            },
            explanation: {
                en: "e4 and d4 each occupy a central square and open lines for a bishop.",
                sv: "e4 och d4 tar båda en central ruta och öppnar en linje för en löpare.",
            },
        },
        exercises: [
            {
                fen: "rnbqkbnr/pppppppp/8/8/4P3/8/PPPP1PPP/RNBQKBNR b KQkq - 0 1",
                moves: ["e7e5", "c7c5"],
                question: {
                    en: "Black to move: challenge White’s central space.",
                    sv: "Svart vid draget: utmana vits plats i centrum.",
                },
                explanation: {
                    en: "e5 meets White in the centre; c5 contests d4 from the flank. These are different sound plans.",
                    sv: "e5 möter vit i centrum; c5 kämpar om d4 från kanten. Det är två olika sunda planer.",
                },
            },
            {
                fen: "rnbqkb1r/ppp1pppp/5n2/3p4/3P4/5N2/PPP1PPPP/RNBQKB1R w KQkq - 2 3",
                moves: ["c2c4"],
                question: {
                    en: "Challenge the black pawn on d5.",
                    sv: "Utmana den svarta bonden på d5.",
                },
                explanation: {
                    en: "c4 puts pressure on d5. If Black captures, White can later build a broader pawn centre.",
                    sv: "c4 sätter press på d5. Om svart slår kan vit senare bygga ett bredare bondecentrum.",
                },
            },
            {
                fen: "rnbqkbnr/pppp1ppp/4p3/8/3PP3/8/PPP2PPP/RNBQKBNR b KQkq - 0 2",
                moves: ["d7d5"],
                question: {
                    en: "Black to move: strike at White’s pawn centre.",
                    sv: "Svart vid draget: utmana vits bondecentrum.",
                },
                explanation: {
                    en: "d5 attacks e4 and claims central space. This is the central idea of the French Defense.",
                    sv: "d5 angriper e4 och tar plats i centrum. Det är den centrala idén i franskt parti.",
                },
            },
        ],
    },
    {
        slug: "king-safety",
        group: "strategy",
        title: {
            en: "Give your king a safe home",
            sv: "Ge kungen ett tryggt hem",
        },
        intro: {
            en: "Castling often protects the king and connects the rooks. Later, a carefully chosen pawn move can create an escape square and prevent back-rank problems.",
            sv: "Rockad skyddar ofta kungen och förenar tornen. Senare kan ett väl valt bondedrag skapa en flyktruta och förebygga problem på sista raden.",
        },
        example: {
            fen: "r1bqk1nr/pppp1ppp/2n5/2b1p3/2B1P3/5N2/PPPP1PPP/RNBQK2R w KQkq - 4 4",
            moves: ["e1g1"],
            question: {
                en: "Move the king to safety and bring the rook into play.",
                sv: "För kungen i säkerhet och få med tornet i spelet.",
            },
            explanation: {
                en: "O-O moves the king behind its pawns and brings the rook to f1.",
                sv: "O-O för kungen bakom bönderna och får tornet till f1.",
            },
        },
        exercises: [
            {
                fen: "r1bqkb1r/1ppp1ppp/p1n2n2/4p3/B3P3/5N2/PPPP1PPP/RNBQK2R w KQkq - 2 5",
                moves: ["e1g1"],
                question: {
                    en: "Castle before starting a new plan.",
                    sv: "Rockera innan du startar en ny plan.",
                },
                explanation: {
                    en: "O-O shelters the king and prepares rook activity. Castling is legal because e1, f1 and g1 are not attacked.",
                    sv: "O-O skyddar kungen och förbereder tornaktivitet. Rockad är tillåten eftersom e1, f1 och g1 inte är angripna.",
                },
            },
            {
                fen: "rnbqk2r/pppp1ppp/4pn2/8/1bPP4/2N1P3/PP3PPP/R1BQKBNR b KQkq - 0 4",
                moves: ["e8g8"],
                question: {
                    en: "Black to move: castle kingside.",
                    sv: "Svart vid draget: gör kort rockad.",
                },
                explanation: {
                    en: "O-O gives the king shelter and brings the rook toward the centre.",
                    sv: "O-O ger kungen skydd och för tornet närmare centrum.",
                },
            },
            {
                fen: "r5k1/8/8/8/8/8/5PPP/5RK1 w - - 0 1",
                moves: ["h2h3", "g2g3"],
                question: {
                    en: "Create an escape square for your king.",
                    sv: "Skapa en flyktruta för kungen.",
                },
                explanation: {
                    en: "h3 frees h2; g3 frees g2. With no immediate threat here, either can give the king room against a later back-rank check.",
                    sv: "h3 frigör h2; g3 frigör g2. Här finns inget omedelbart hot, och båda kan ge kungen plats mot en senare schack på sista raden.",
                },
            },
        ],
    },
    {
        slug: "piece-activity",
        group: "strategy",
        title: {
            en: "Improve a quiet piece",
            sv: "Förbättra en passiv pjäs",
        },
        intro: {
            en: "When there is no forcing tactic, look for a piece that is doing little. Knights like useful central squares, bishops need diagonals, and rooks need open files.",
            sv: "När det inte finns någon tvingande taktik, leta efter en pjäs som gör lite. Springare behöver användbara centrala rutor, löpare diagonaler och torn öppna linjer.",
        },
        example: {
            fen: "r1bq1rk1/bpp2pp1/p1np1n1p/4p3/4P3/1BPP1N2/PP1N1PPP/R1BQR1K1 w - - 0 10",
            moves: ["d2f1"],
            question: {
                en: "Begin rerouting the d2 knight toward g3 or e3.",
                sv: "Börja föra springaren på d2 mot g3 eller e3.",
            },
            explanation: {
                en: "Nf1 begins a useful manoeuvre. From f1 the knight can reach g3 or e3 and increase pressure on central squares.",
                sv: "Sf1 inleder en användbar manöver. Från f1 kan springaren nå g3 eller e3 och öka trycket på centrala rutor.",
            },
        },
        exercises: [
            {
                fen: "r1bqrnk1/pp2bppp/2p2n2/3p2B1/3P4/2NBPN2/PPQ2PPP/R4RK1 w - - 6 11",
                moves: ["a1e1", "f1e1"],
                question: {
                    en: "Put a rook on e1 to support a future e3–e4 pawn break.",
                    sv: "Placera ett torn på e1 för att stödja ett framtida bondebrott med e3–e4.",
                },
                explanation: {
                    en: "Rae1 or Rfe1 supports the e-pawn when the e-file opens. Both rooks can help this plan; first check the safety of any pawn advance.",
                    sv: "Tae1 eller Tfe1 stödjer e-bonden när e-linjen öppnas. Båda tornen kan hjälpa denna plan; kontrollera först att en bondeframstöt är säker.",
                },
            },
            {
                fen: "rnbqkb1r/ppp2ppp/8/3np3/8/2N3P1/PP1PPP1P/R1BQKBNR w KQkq - 0 5",
                moves: ["f1g2"],
                question: {
                    en: "Bring the bishop to the long diagonal.",
                    sv: "För löparen till den långa diagonalen.",
                },
                explanation: {
                    en: "Bg2 gives the bishop scope along g2–a8 and helps control central squares. Its influence may increase as the centre opens.",
                    sv: "Lg2 ger löparen utrymme längs g2–a8 och hjälper till att kontrollera centrala rutor. Dess inflytande kan öka när centrum öppnas.",
                },
            },
            {
                fen: "r2q1rk1/bpp2pp1/p1npbn1p/4p3/4P3/1BPP1N2/PP3PPP/R1BQRNK1 w - - 2 11",
                moves: ["f1g3", "f1e3"],
                question: {
                    en: "Complete the knight manoeuvre by moving toward central squares.",
                    sv: "Slutför springarmanövern genom att flytta mot centrala rutor.",
                },
                explanation: {
                    en: "Ng3 and Ne3 both increase the knight’s influence over f5. Improving a piece can take more than one move.",
                    sv: "Sg3 och Se3 ökar båda springarens inflytande över f5. Att förbättra en pjäs kan kräva mer än ett drag.",
                },
            },
        ],
    },
    {
        slug: "basic-checkmates",
        group: "endgames",
        title: {
            en: "Finish with king and major piece",
            sv: "Avsluta med kung och tung pjäs",
        },
        intro: {
            en: "Use your king to control escape squares and your queen or rook to deliver check. A position is mate only when the king is checked and has no legal reply.",
            sv: "Använd kungen för att kontrollera flyktrutor och damen eller tornet för att schacka. Det är matt först när kungen är schackad och saknar lagligt svar.",
        },
        example: {
            fen: "7k/5Q2/6K1/8/8/8/8/8 w - - 0 1",
            moves: ["f7g7"],
            question: {
                en: "Deliver a protected queen checkmate.",
                sv: "Gör matt med en skyddad dam.",
            },
            explanation: {
                en: "Qg7# is protected by Kg6 and covers every escape square. An unprotected queen beside the king could be captured.",
                sv: "Dg7# är skyddad av Kg6 och täcker alla flyktrutor. En oskyddad dam intill kungen skulle kunna tas.",
            },
        },
        exercises: [
            {
                fen: "7k/8/6K1/8/8/8/8/5R2 w - - 0 1",
                moves: ["f1f8"],
                question: {
                    en: "Use your king and rook together to give mate.",
                    sv: "Använd kung och torn tillsammans för att göra matt.",
                },
                explanation: {
                    en: "Rf8# controls the eighth rank while Kg6 covers g7 and h7.",
                    sv: "Tf8# kontrollerar åttonde raden medan Kg6 täcker g7 och h7.",
                },
            },
            {
                fen: "k7/8/1QK5/8/8/8/8/8 w - - 0 1",
                moves: ["b6b7"],
                question: {
                    en: "Give mate with a queen protected by your king.",
                    sv: "Gör matt med en dam som skyddas av kungen.",
                },
                explanation: {
                    en: "Qb7# covers a7 and b8, and Kc6 protects the queen on b7.",
                    sv: "Db7# täcker a7 och b8, och Kc6 skyddar damen på b7.",
                },
            },
            {
                fen: "8/8/8/8/8/6k1/5q2/7K b - - 0 1",
                moves: ["f2g2"],
                question: {
                    en: "Black to move: coordinate king and queen for mate.",
                    sv: "Svart vid draget: samordna kung och dam för matt.",
                },
                explanation: {
                    en: "Qg2# is protected by Kg3 and controls every square around Kh1.",
                    sv: "Dg2# skyddas av Kg3 och kontrollerar varje ruta runt Kh1.",
                },
            },
        ],
    },
    {
        slug: "king-and-pawn",
        group: "endgames",
        title: {
            en: "Help a pawn become a queen",
            sv: "Hjälp en bonde att bli dam",
        },
        intro: {
            en: "In a pawn ending, your king is an active piece. Opposition can force the other king to give way; a passed pawn can then advance toward promotion.",
            sv: "I ett bondeslutspel är kungen en aktiv pjäs. Opposition kan tvinga den andra kungen att vika undan; en fribonde kan sedan gå mot förvandling.",
        },
        example: {
            fen: "8/8/4k3/8/3K4/4P3/8/8 w - - 0 1",
            moves: ["d4e4"],
            question: {
                en: "Take the opposition: put one square between the kings.",
                sv: "Ta oppositionen: lämna en ruta mellan kungarna.",
            },
            explanation: {
                en: "Ke4 faces Ke6 with e5 between them. Black must move, so its king has to give way.",
                sv: "Ke4 står mitt emot Ke6 med e5 mellan dem. Svart måste flytta, så dess kung måste vika undan.",
            },
        },
        exercises: [
            {
                fen: "8/8/3k4/8/2K5/3P4/8/8 w - - 0 1",
                moves: ["c4d4"],
                question: {
                    en: "Take opposition in front of your pawn.",
                    sv: "Ta oppositionen framför din bonde.",
                },
                explanation: {
                    en: "Kd4 places the kings on one file with a square between them. Black is to move.",
                    sv: "Kd4 placerar kungarna på samma linje med en ruta mellan dem. Svart är vid draget.",
                },
            },
            {
                fen: "8/8/4p3/5k2/8/4K3/8/8 b - - 0 1",
                moves: ["f5e5"],
                question: {
                    en: "Black to move: take the opposition.",
                    sv: "Svart vid draget: ta oppositionen.",
                },
                explanation: {
                    en: "Ke5 faces Ke3 with e4 between them. White must give way.",
                    sv: "Ke5 står mitt emot Ke3 med e4 mellan dem. Vit måste vika undan.",
                },
            },
            {
                fen: "k7/4P3/4K3/8/8/8/8/8 w - - 0 1",
                moves: ["e7e8q"],
                question: {
                    en: "Promote your passed pawn to a queen.",
                    sv: "Förvandla din fribonde till dam.",
                },
                explanation: {
                    en: "e8=Q promotes the pawn. Choose the queen here to obtain decisive material.",
                    sv: "e8=D förvandlar bonden. Välj dam här för att få ett avgörande materialövertag.",
                },
            },
        ],
    },
];
const foundationMetadata = {
    "hanging-pieces": {
        difficulty: "beginner",
        prerequisites: ["pawn-moves"],
        skill: "material",
    },
    forks: {
        difficulty: "beginner",
        prerequisites: ["knight-moves"],
        skill: "forks",
    },
    pins: {
        difficulty: "intermediate",
        prerequisites: ["checks-and-safety"],
        skill: "pins",
    },
    skewers: {
        difficulty: "intermediate",
        prerequisites: ["checks-and-safety"],
        skill: "skewers",
    },
    "discovered-attacks": {
        difficulty: "intermediate",
        prerequisites: ["checks-and-safety"],
        skill: "discovered-attacks",
    },
    "back-rank-mates": {
        difficulty: "beginner",
        prerequisites: ["checkmate-and-draws"],
        skill: "checkmate",
    },
    development: {
        difficulty: "beginner",
        prerequisites: ["special-moves"],
        skill: "development",
    },
    "centre-control": {
        difficulty: "beginner",
        prerequisites: ["pawn-moves"],
        skill: "centre-control",
    },
    "king-safety": {
        difficulty: "beginner",
        prerequisites: ["special-moves"],
        skill: "king-safety",
    },
    "piece-activity": {
        difficulty: "intermediate",
        prerequisites: ["development"],
        skill: "piece-activity",
    },
    "basic-checkmates": {
        difficulty: "beginner",
        prerequisites: ["checkmate-and-draws"],
        skill: "checkmate",
    },
    "king-and-pawn": {
        difficulty: "intermediate",
        prerequisites: ["basic-checkmates"],
        skill: "pawn-endgames",
    },
};
export const learningLessons = [
    ...basicsLessons,
    ...foundationLessons.map((lesson) => (Object.assign(Object.assign({}, lesson), foundationMetadata[lesson.slug]))),
];
//# sourceMappingURL=learningCurriculum.js.map